/* Exercise — artboard 1d, made real.

   The artboard shows a static editor with a pre/post Run state. Here the code
   is editable and Run actually executes it against the trials (see lib/runner).
*/

import { useState, useRef } from 'react';
import s from './Exercise.module.css';
import { Kicker, Button, Code } from '../design/primitives.jsx';
import { inline } from '../content/inline.jsx';
import { runTrials } from '../lib/runner.js';
import { useProgress } from '../lib/progress.jsx';

const cx = (...v) => v.filter(Boolean).join(' ');

function Editor({ value, onChange }) {
  const lines = value.split('\n').length;
  const layer = useRef(null);
  const gutter = useRef(null);

  /* The textarea is transparent; the text you see is the highlighted <pre>
     beneath it. When a long line scrolls the textarea, the <pre> has to scroll
     with it, or the caret moves through text that stays cut off. */
  const onScroll = e => {
    const { scrollLeft, scrollTop } = e.target;
    if (layer.current) { layer.current.scrollLeft = scrollLeft; layer.current.scrollTop = scrollTop; }
    if (gutter.current) gutter.current.scrollTop = scrollTop;
  };

  /* Tab inserts two spaces rather than moving focus. Shift-Tab still escapes,
     so the editor never becomes a keyboard trap. */
  const onKeyDown = e => {
    if (e.key !== 'Tab' || e.shiftKey) return;
    e.preventDefault();
    const el = e.target;
    const { selectionStart: a, selectionEnd: b } = el;
    onChange(value.slice(0, a) + '  ' + value.slice(b));
    requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = a + 2; });
  };

  return (
    <div className={s.editor}>
      <div className={s.gutter} ref={gutter} aria-hidden="true">
        {Array.from({ length: lines }, (_, i) => <div key={i}>{i + 1}</div>)}
      </div>
      <div className={s.editorBox}>
        <pre className={s.layer} ref={layer} aria-hidden="true"><Code>{value}</Code></pre>
        <textarea className={s.input} value={value} spellCheck={false}
                  aria-label="Your solution"
                  onChange={e => onChange(e.target.value)} onKeyDown={onKeyDown}
                  onScroll={onScroll} />
      </div>
    </div>
  );
}

/* layout — 'three' (a trial page, 1560px wide): brief | editor | console.
   'practice' (a practice step inside a lesson): the band breaks out of the
   lesson's 1024px column, and the brief, trials and console share the right
   column, so the editor gets the width (Nick, 2026-10-08). */
export default function Exercise({ exercise, lessonId, onPass, layout = 'three' }) {
  const { complete } = useProgress();
  const [code, setCode] = useState(exercise.starter);
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);

  const results = run?.results || [];
  const passed = run?.ok && results.length > 0 && results.every(r => r.pass);

  async function handleRun() {
    setBusy(true);
    const out = await runTrials({
      code, exportName: exercise.exportName, tests: exercise.tests,
      timeout: exercise.timeout,          // undefined → the runner's default, which scales with the tests
    });
    setBusy(false);
    setRun(out);
    if (out.ok && out.results.every(r => r.pass)) {
      if (lessonId) complete(lessonId);
      onPass?.();
    }
  }

  const lines = !run
    ? [['$ node trials.test.js', 'dim'], ['waiting for run…', 'dim']]
    : [
        ['$ node trials.test.js', 'dim'],
        ...run.logs.map(l => [l.text, l.kind === 'err' ? 'err' : 'dim']),
        ...(run.error ? [[run.error, 'err']] : []),
        ...results.map((r, i) => [
          `${r.pass ? '✓' : '✗'} ${exercise.tests[i].label}${r.error ? ` — ${r.error}` : ''}`,
          r.pass ? 'ok' : 'err',
        ]),
        ...(run.ok ? [[`${results.filter(r => r.pass).length} of ${results.length} passing`, 'dim']] : []),
      ];

  const trials = (
    <div className={s.trials}>
      {exercise.tests.map((t, i) => {
        const r = results[i];
        return (
          <div key={i} className={cx(s.trial, r && (r.pass ? s.pass : s.fail))}>
            <span className={s.trialMark}>{r ? (r.pass ? '✓' : '✗') : '•'}</span>
            <span className={s.trialLabel}>{t.label}</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={cx(s.exercise, s[layout])}>
      <div className={s.bar}>
        <div className={s.barLeft}>
          <span className={s.lamp} aria-hidden="true" />
          <div>
            <Kicker tone="onDark">
              {exercise.kicker}
            </Kicker>
            <div className={s.barTitle}>{inline(exercise.title)}</div>
          </div>
        </div>
        <div className={s.barRight}>
          <span className={s.pips}>
            {exercise.tests.map((t, i) => (
              <span key={i} className={cx(s.pip, results[i] && (results[i].pass ? s.pass : s.fail))} />
            ))}
          </span>
        </div>
      </div>

      <div className={s.grid}>
        <div className={s.brief}>
          <Kicker tone="lemon">BRIEF</Kicker>
          <p className={s.briefText}>{inline(exercise.brief)}</p>

          <div className={s.sectionGap}><Kicker tone="lemon">TRIALS</Kicker></div>
          {trials}

          {exercise.hint && (
            <div className={s.hint}>
              <button type="button" className={s.hintToggle}
                      aria-expanded={hintOpen} onClick={() => setHintOpen(o => !o)}>
                Hint {hintOpen ? '· hide' : '· reveal'}
              </button>
              {hintOpen && <p className={s.hintBody}>{inline(exercise.hint)}</p>}
            </div>
          )}
        </div>

        <div className={s.editorPane}>
          <div className={s.tabs}><span className={s.tab}>{exercise.filename || 'solution.js'}</span></div>
          <Editor value={code} onChange={setCode} />
          <div className={s.editorFoot}>
            <span className={s.runtime}>runs in a worker · {exercise.timeout ?? 2000 + (exercise.tests?.length || 0) * 1000}ms limit</span>
            <div className={s.runActions}>
              <Button variant="ghostDark" onClick={() => { setCode(exercise.starter); setRun(null); }}>
                Reset
              </Button>
              <Button variant="accent" onClick={handleRun} disabled={busy}>
                {busy ? 'Running…' : run ? 'Run again' : 'Run trials'}
              </Button>
            </div>
          </div>
        </div>

        <div className={s.console}>
          <Kicker tone="lemon">CONSOLE</Kicker>
          <div className={s.well} role="log" aria-live="polite">
            {lines.map(([text, kind], i) => (
              <div key={i} className={cx(s.line, kind === 'ok' && s.ok, kind === 'err' && s.err)}>{text}</div>
            ))}
          </div>
          <div className={cx(s.verdict, passed && s.pass, run && !passed && s.fail)}>
            <div className={s.verdictTitle}>
              {passed ? 'TRIAL CLEARED' : run ? 'NOT YET' : 'NOT YET RUN'}
            </div>
            <p className={s.verdictBody}>
              {passed ? (exercise.success || 'Every trial passes. On to the next one.')
                : run ? 'Some trials are still failing — read the console and try again.'
                : 'Run the trials to check your solution against every case.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
